// Re-Issue Bulk Membership Passes — superadmin, club-scoped.
//
// Deliberately the same layout and idioms as BulkPassCreation.js, with one
// difference: there is NO validate step. The CSV goes straight to the server,
// so Submit is enabled as soon as a file is chosen.
//
// CSV: Member number, Mobile number — one record per line, no header row, and
// the mobile carries an explicit +<country code> prefix.
//
// The whole job lifecycle — the 202 + jobid contract, polling, resuming a
// running job on arrival, attaching to a refused duplicate, and the rule for
// when the error file may be offered — lives in useBulkJob, shared with the
// other two bulk pages. The output panels come from BulkJobStatus, so all three
// pages report progress identically. This page owns only its own chrome and
// payload.
import React, { useEffect, useState, useContext } from 'react';
import { Grid, Box, Paper, Button, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useUrlContext } from '../context/urlContext';
import { errorContext } from '../Components/ErrorDisplay';
import { useAuth } from '../context/authContext';
import useBulkJob from '../Components/useBulkJob';
import BulkJobStatus from '../Components/BulkJobStatus';
import { JOB_PROCESS } from '../Components/useJobPoller';

export default function ReIssuePassCreation() {
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const history = useHistory();
    const { setErrorMessage } = useContext(errorContext);

    const [csvFile, setCsvFile] = useState(null);

    const job = useBulkJob({
        processCode: JOB_PROCESS.REISSUE_PASS,
        fallbackErrorMessage: 'There were errors re-issuing passes.',
    });
    const { submitting, submittedFileName, submitJob, resetJob } = job;

    useEffect(() => {
        setErrorMessage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // No validation step, so a chosen file is all that gates Submit. `submitting`
    // covers both the upload and the whole polling run, so a second import
    // cannot be fired while one is in flight.
    const isSubmitEnabled = csvFile !== null && !submitting;

    function handleChangeInFile(e) {
        resetJob();
        const file = e.target.files[0];
        setCsvFile(file || null);
    }

    async function handleSubmit() {
        const data = new FormData();
        // NOTE: this endpoint takes `issuer_id`, not the `clubid` that
        // csvBulkSubmit uses. Same value, different field name — don't "align"
        // it with the bulk page.
        data.append('issuer_id', user.issuerId);
        data.append('image', csvFile);

        const chosenName = csvFile.name;
        // Clearing the file is what stops a second click re-issuing the same
        // passes; the name lives on in the job state.
        setCsvFile(null);

        await submitJob(UrlHelperObj.reIssuePassUrl(), data, chosenName);

        const fileInput = document.getElementById('reissue-csv-upload');
        if (fileInput) fileInput.value = '';
    }

    return (
        <Grid container xs={12} spacing={0} style={{ borderStyle: 'none', justifyContent: 'flex-start', marginLeft: 10 }}>
            <Grid container item xs={12} style={{ textAlign: 'left', borderStyle: 'none' }}>
                <Grid item xs={12}>
                    <Paper style={{ padding: '8px 12px' }}>
                        <Typography variant="h5" style={{ textAlign: 'left' }}>Re-Issue Membership Passes in Bulk</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Instructions */}
            <Grid container xs={12} direction="column" sx={{ mb: 1, alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '20px', width: '100%', maxWidth: '700px',
                    padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff',
                }}>
                    <Typography>Please select CSV file for re-issuing membership passes</Typography>
                </Grid>
            </Grid>

            {/* File picker + expected headers */}
            <Grid container xs={12} direction="column" sx={{ mb: 0, alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '10px', width: '100%', maxWidth: '700px',
                    padding: '10px', borderRadius: '5px', backgroundColor: '#f0f8ff',
                }}>
                    {/* Disabled while a job is in flight. The `disabled` on the
                        INPUT is what actually stops it: a label click on a
                        disabled control does not open the picker. The one on the
                        Button is for the greyed-out look. */}
                    <input
                        id="reissue-csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleChangeInFile}
                        disabled={submitting}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="reissue-csv-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            disabled={submitting}
                            sx={{ minWidth: 160, height: 26 }}
                        >
                            Choose CSV File
                        </Button>
                    </label>
                    <Typography style={{ marginTop: '8px', textAlign: 'left' }}>
                        <span style={{ fontWeight: 700 }}>Expected CSV headers:</span>
                        <br />
                        Member number, Mobile number
                         <br />
                        One record per line. No header required.
                        <br/>
                        <span style={{ fontWeight: 700 }}>Mobile Number format:</span> "+" "Country code" "Mobile Number"
                        <br/>
                        <span style={{ fontWeight: 700 }}>Example:</span> +61456789012
                    </Typography>
                </Grid>
            </Grid>

            {/* Selected file name */}
            <Grid container item xs={12}>
                <Grid item xs={12} style={{
                    textAlign: 'left', marginTop: '10px',
                    padding: '5px', borderRadius: '5px', backgroundColor: '#f0f8ff',
                }}>
                    <Box sx={{ display: 'flex', gap: 10, alignItems: 'flex-start', maxWidth: 800 }}>
                        <Box sx={{ flex: 1, maxWidth: 500 }}>
                            <Typography
                                sx={{
                                    border: '1px dashed #ccc', borderRadius: 1, p: 1, ml: '5px',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'flex-start', textAlign: 'left',
                                }}
                                color="text.secondary"
                            >
                                {/* After submit csvFile is null, so fall back to the
                                    submitted name — the result below refers to it. */}
                                {csvFile ? csvFile.name : (submittedFileName || 'No file selected')}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <BulkJobStatus {...job} onDownloadErrorFile={job.downloadErrorFile} />
            </Grid>

            {/* Submit / Back */}
            <Grid container xs={12} direction="column" sx={{ mb: 0, alignItems: 'flex-start' }}>
                <Grid item style={{
                    textAlign: 'left', marginTop: '10px', width: '100%', maxWidth: '700px',
                    padding: '10px', borderRadius: '5px',
                }}>
                    {/* The global .MuiButton-containedPrimary !important rule in
                        theme/styles.css overrides the disabled state, so the greyed
                        look is forced here — same workaround as BulkPassCreation. */}
                    <Button
                        type="submit"
                        disabled={!isSubmitEnabled}
                        variant="contained"
                        component="span"
                        onClick={handleSubmit}
                        style={{
                            marginLeft: '-8px',
                            backgroundColor: !isSubmitEnabled ? '#bdbdbd' : 'blue',
                            color: !isSubmitEnabled ? '#616161' : '#ffffff',
                            opacity: 1,
                            boxShadow: !isSubmitEnabled ? 'none' : undefined,
                            cursor: !isSubmitEnabled ? 'not-allowed' : undefined,
                        }}
                        sx={{
                            '&.Mui-disabled': {
                                backgroundColor: '#bdbdbd !important',
                                color: '#616161 !important',
                                opacity: 1,
                            },
                        }}
                    >
                        {submitting ? (job.isPolling ? 'Processing…' : 'Submitting…') : 'Submit'}
                    </Button>
                    {/* "Back", not "Cancel": this only navigates — it has never
                        stopped anything. During a running import "Cancel" would
                        read as "abort this import", and the job would complete
                        regardless. Left enabled deliberately: the drawer is
                        clickable during a job, so disabling the one button that
                        also navigates would achieve nothing. */}
                    <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        onClick={() => history.push(user?.issuerId ? `/club/${user.issuerId}/home` : '/')}
                        style={{ marginLeft: '8px' }}
                    >
                        Back
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}
